// eslint-disable-next-line
///<reference path="../../../../../jsdoc-types.js" />

import deepmerge from 'deepmerge';
import { getMasterProductId, getVariationGroupId } from './productIdHelpers';

const { isObject } = require('../utils/type');

/**
 * Get variation attribute data for a given attr id
 * @param {Object} product The product
 * @param {string} attributeId the ID of the target variation attribute
 * @returns {Object|undefined} Variation attribute
 */
export const getVariationAttributeFromProduct = (product, attributeId) =>
  product?.variationAttributes?.find(
    (attr) => attr != null && attr.id === attributeId
  ) ?? undefined;

/**
 * Check if an attribute is color related
 *
 * @param {VariationAttribute} variationAttribute attribute to check
 * @return {boolean}  true or false
 */
export const isColorVariationAttribute = (variationAttribute) =>
  variationAttribute.id === 'color';

/**
 * Check if two products shares master product
 *
 * Examples:
 *  True
 *    201BR48P3.T1000M  &  201BR48P3.T1002L
 *    201BR48P3.T1000   &  201BR48P3.T1002L
 *    201BR48P3         &  201BR48P3.T1002L
 *  False
 *    201BR48P3.T1000M  &  202BR9051.T6054
 *
 * @param {Object|string} productA one product or its pid
 * @param {Object|string} productB another product or its pid
 * @return {boolean}  true or false
 */
export const isSameMasterProduct = (productA, productB) => {
  const pidA = isObject(productA) ? productA.id : productA;
  const pidB = isObject(productB) ? productB.id : productB;
  return getMasterProductId(pidA) === getMasterProductId(pidB);
};

/**
 * Check if two products shares variation group
 *
 * Examples:
 *  True
 *    201BR48P3.T1000M  &  201BR48P3.T1000L
 *    201BR48P3.T1000   &  201BR48P3.T1000L
 *  False
 *    201BR48P3.T1000M  &  201BR48P3.T1002L
 *    202BR9051.T1000M  &  201BR48P3.T1000M
 *
 * @param {Object|string} productA one product or its pid
 * @param {Object|string} productB another product or its pid
 * @return {boolean}  true or false
 */
export const isSameVariationGroup = (productA, productB) => {
  const pidA = isObject(productA) ? productA.id : productA;
  const pidB = isObject(productB) ? productB.id : productB;
  return getVariationGroupId(pidA) === getVariationGroupId(pidB);
};

/**
 * Check if two products are the same variation.
 *
 * Examples:
 *  True
 *    201BR48P3.T1000M  &  201BR48P3.T1000M
 *  Otherwise, false
 *
 * @param {Object|string} productA one product or its pid
 * @param {Object|string} productB another product or its pid
 * @return {boolean}  true or false
 */
export const isSameVariationProduct = (productA, productB) => {
  const pidA = isObject(productA) ? productA.id : productA;
  const pidB = isObject(productB) ? productB.id : productB;
  return pidA === pidB;
};

/**
 * Check if two products are different colors of the same master product.
 *
 * @param {Object|string} productA one product or its pid
 * @param {Object|string} productB another product or its pid
 * @return {boolean}  true or false
 */
export const isColorVariation = (productA, productB) =>
  isSameMasterProduct(productA, productB) &&
  !isSameVariationGroup(productA, productB);

/**
 * Check if two products are different sizes of the same color (variation group).
 *
 * @param {Object|string} productA one product or its pid
 * @param {Object|string} productB another product or its pid
 * @return {boolean}  true or false
 */
export const isSizeVariation = (productA, productB) =>
  isSameVariationGroup(productA, productB) &&
  !isSameVariationProduct(productA, productB);

// TODO This function must be reviewed by the team.
// Options:
//  - Keep running like Object.assign (modifying the target)
//  - Remove side-effect and return a new Object
/**
 * Adds a new property 'upid' to a given product.
 * ! Modifies the object, it does not return a new one.
 * @param {Object} product product model or variation
 * @return {Object} given object after being modified
 */
export const assignUpidToProduct = (product) => {
  if (product) {
    // Disabling note: This function is designed to update the given product
    // eslint-disable-next-line no-param-reassign
    product.upid = getMasterProductId(product.id);
  }
  return product;
};

/**
 *
 * @param {FullProduct} target - current target
 * @param {FullProduct} productVariation - data from product variation endpoint
 * @returns {FullProduct} - mixed product
 */
export function mergeProducts(target, productVariation) {
  if (!isSameMasterProduct(target, productVariation)) {
    throw new Error('You cannot merge products from different master products');
  }
  // we need to keep variation colors from first product in grid
  const variationAttributes = productVariation.variationAttributes.map(
    (attr) =>
      isColorVariationAttribute(attr)
        ? target.variationAttributes.find(isColorVariationAttribute)
        : attr
  );

  // we need to merge data because color.url doesnt return all data we need
  // cannot simply replace the object because we would lose data (images, ...)
  // deepmerge needed because we have non-enumerable properties

  const product = deepmerge(target, productVariation);
  // keep the new images
  product.bylImages = productVariation.bylImages;

  // we have to just keep the new variationAttributes though
  product.variationAttributes = variationAttributes;

  return product;
}
